<template>
    <canvas :id='uniqueCanvasId + renderingKey' class='hand-cursor prevent-scrolling-on-touch' :ref='uniqueCanvasId + renderingKey' :width='canvasWidth' :height='canvasHeight' @mousedown='canvasMouseDown' @mouseup='canvasMouseUp' @mousemove='canvasMouseMove' @mouseout='canvasMouseOut' @touchstart.self='onTouchStart' @touchend.self='onTouchEnd' @touchmove.self='onTouchMove' />
</template>
<script>

import utilityService from '../mixins/utilityService.js'

export default {
    // https://stackoverflow.com/questions/40410332/vuejs-access-child-components-data-from-parent
    // https://stackoverflow.com/questions/45478035/start-range-in-v-for-n-in-10-from-zero
    props: {
        // either rowsCount or columnsCount must be 1, this is a single dimensional grid and colors data array
        rowsCount: {
            type: Number,
            required: true
        },
        columnsCount: {
            type: Number,
            required: true
        },
        itemsCount: {
            type: Number,
            required: true
        },
        cellPixelSize: {
            type: Number,
            required: true
        },
        borderThicknessPixels: {
            type: Number,
            required: true
        },
        borderColor: {
            type: String,
            required: true
        },
        colorsArray: {  // incoming array is always 0..max ordering, for display purposes, parent component must reverse order as needed for presentation
            type: Array,
            required: true
        },
        currentColor: {
            type: Object,
            required: true
        },
        uniqueCanvasId: {
            type: String,
            required: true
        },
        renderingKey: {
            type: String,
            required: true
        },
        isSearchAndReplace: {
            type: Boolean,
            required: true
        },
        isCopy: {
            type: Boolean,
            required: true
        },
        isPaste: {
            type: Boolean,
            required: true
        },
        isReadOnly: {
            type: Boolean,
            required: true
        },
        copiedColorsArray: {
            type: Array,
            required: true
        }
    },
    mixins: [utilityService],
    components: {
    },
    data() {
        return {
            vueCanvas: null,
            mouseDownItemNumber: -1,
            lastKnownTouchEvent: null
        }
    },
    methods: {
        renderGrid() {
            this.vueCanvas.fillStyle = this.borderColor
            for (let rowIndex=0; rowIndex <= this.rowsCount; rowIndex++) {
                this.vueCanvas.fillRect(0, this.pixelYFromRowNum(rowIndex), this.canvasWidth, this.borderThicknessPixels)
            }

            for (let colIndex=0; colIndex <= this.columnsCount; colIndex++) {
                this.vueCanvas.fillRect(this.pixelXFromColNum(colIndex), 0, this.borderThicknessPixels, this.canvasHeight)
            }

            let itemNumberIndex = 0
            for (let rowIndex=0; rowIndex < this.rowsCount; rowIndex++) {
                let cellY = this.pixelYFromRowNum(rowIndex) + this.borderThicknessPixels
                for (let colIndex=0; colIndex < this.columnsCount; colIndex++) {
                    let cellX = this.pixelXFromColNum(colIndex) + this.borderThicknessPixels
                    let cellWidth = this.cellPixelSize
                    let cellHeight = this.cellPixelSize
                    let color = this.colorsArray[itemNumberIndex]
                    this.vueCanvas.fillStyle='rgb(' + color.R + ',' + color.G + ',' + color.B + ')'
                    this.vueCanvas.fillRect(cellX, cellY, cellWidth, cellHeight)
                    itemNumberIndex++
                }
            }
        },

        canvasMouseDown(e) {
            // console.log('in canvas mouse down')
            this.mouseDownItemNumber = this.canvasItemNumber(e)
            if (this.isSearchAndReplace) {
                this.replaceAllInstancesOfCellColor(this.mouseDownItemNumber)
                this.mouseDownItemNumber = -1
            }
            if (this.isPaste) {
                this.pasteSelectedColors(this.mouseDownItemNumber)
                this.mouseDownItemNumber = -1
            }
        },

        canvasMouseUp(e) {
            let mouseUpItemNumber = this.canvasItemNumber(e)
            if (!this.isSearchAndReplace && this.mouseDownItemNumber > -1 && ((!this.isReadOnly) || this.isCopy || this.isPaste)) {
                let startItem = Math.min(this.mouseDownItemNumber, mouseUpItemNumber)
                let endItem = Math.max(this.mouseDownItemNumber, mouseUpItemNumber)
                if (this.isCopy) {
                    this.copySelectedColors(startItem, endItem)
                }
                else if (!this.isPaste) {
                    this.changeCellsColor(startItem, endItem)
                }
                this.renderGrid() // erases any prior bounding box while dragging
                this.mouseDownItemNumber = -1
            }
        },

        canvasMouseMove(e) {
            if ((!this.isSearchAndReplace) && (!this.isPaste) && this.mouseDownItemNumber > -1 && e.buttons===1 && ((!this.isReadOnly) || this.isCopy || this.isPaste)) {  // e.buttons===1 means primary button is down
                let currentItemNumber = this.canvasItemNumber(e)
                this.renderGrid() // erases any prior bounding box while dragging
                this.drawBoundingBox(this.mouseDownItemNumber, currentItemNumber)
            }
            else if (this.isPaste) {
                this.renderGrid() // erases any prior bounding box while moving
                let startItemNumber = this.canvasItemNumber(e)

                let endItemNumber = this.isHorizontal ? startItemNumber - this.copiedColorsArray.length + 1 : startItemNumber + this.copiedColorsArray.length - 1
                console.log('mouse move: startItemNumber: ' + startItemNumber + ', endItemNumber: ' + endItemNumber)

                if (endItemNumber < 0) {
                    endItemNumber = 0
                }
                this.drawBoundingBox(startItemNumber, endItemNumber)
            }
        },

        canvasMouseOut(e) {
            if (!this.isSearchAndReplace) {
                this.renderGrid() // erases any prior bounding box while dragging
                // console.log('in canvasMouseOut')
                // this.mouseDownItemNumber = -1
            }
        },

        canvasItemNumber(e) {
            let canvasElement = this.$refs[this.uniqueCanvasId + this.renderingKey]
            let canvasCurrentRect = canvasElement.getBoundingClientRect()
            let canvasX = Math.trunc(e.clientX - canvasCurrentRect.left)
            let canvasY = Math.trunc(e.clientY - canvasCurrentRect.top)
            let rowNum = this.rowNumFromPixelY(canvasY)
            let colNum = this.colNumFromPixelX(canvasX)
            let itemNumberIndex = (this.isVertical) ? rowNum : colNum
            return itemNumberIndex
        },

        onTouchStart(e) {
            var touch = e.touches[0]
            this.lastKnownTouchEvent = touch
            var mouseEvent = new MouseEvent('mousedown', { clientX: touch.clientX, clientY: touch.clientY })
            let canvas = this.$refs[this.uniqueCanvasId + this.renderingKey]
            canvas.dispatchEvent(mouseEvent)
        },

        onTouchEnd(e) {
            // console.log('in onTouchEnd, e: ' + JSON.stringify(e))
            var touch = this.lastKnownTouchEvent
            var mouseEvent = new MouseEvent('mouseup', { clientX: touch.clientX, clientY: touch.clientY })
            let canvas = this.$refs[this.uniqueCanvasId + this.renderingKey]
            canvas.dispatchEvent(mouseEvent)

            mouseEvent = new MouseEvent('mouseout', { clientX: touch.clientX, clientY: touch.clientY })
            canvas.dispatchEvent(mouseEvent)
        },

        onTouchMove(e) {
            var touch = e.touches[0]
            this.lastKnownTouchEvent = touch
            var mouseEvent = new MouseEvent('mousemove', { clientX: touch.clientX, clientY: touch.clientY, buttons: 1 })
            let canvas = this.$refs[this.uniqueCanvasId + this.renderingKey]
            canvas.dispatchEvent(mouseEvent)
        },

        changeCellsColor(startItemIndex, endItemIndex) {
            // console.log('in changeCellColor, startItemIndex: ' + startItemIndex + ', endItemIndex: ' + endItemIndex)
            for (let i = startItemIndex; i <= endItemIndex; i++) {
                let newColorValue = { R: this.currentColor.R, G: this.currentColor.G, B: this.currentColor.B }
                this.colorsArray[i] = newColorValue
            }

            // reactivity didn't work even with Vue.set for array assignments, just do this instead
            this.renderGrid()
            this.$emit('colorDataChanged', { startThreadIndex: startItemIndex, endThreadIndex: endItemIndex }) // lets the parent know so other draft components like the drawdown can be redrawn
        },

        copySelectedColors(startItemIndex, endItemIndex) {
            let newCopiedColorsArray = []
            if (this.isVertical) { // copy colors forwards
                for (let c = startItemIndex; c <= endItemIndex; c++) {
                    let newColor = {}
                    newColor.R = this.colorsArray[c].R
                    newColor.G = this.colorsArray[c].G
                    newColor.B = this.colorsArray[c].B
                    newCopiedColorsArray.push(newColor)
                }
            }
            else { // copy colors backwards
                for (let c = endItemIndex; c >= startItemIndex; c--) {
                    let newColor = {}
                    newColor.R = this.colorsArray[c].R
                    newColor.G = this.colorsArray[c].G
                    newColor.B = this.colorsArray[c].B
                    newCopiedColorsArray.push(newColor)
                }
            }
            this.renderGrid()
            this.$emit('colorsCopied', { copiedColorsArray: newCopiedColorsArray })
        },

        pasteSelectedColors(startItemIndex) {
            let c = startItemIndex
            if (this.isVertical) {
                let colorIndex = 0
                for (let p = 0; p < this.copiedColorsArray.length && c < this.colorsArray.length; p++) {
                    this.colorsArray[c].R = this.copiedColorsArray[colorIndex].R
                    this.colorsArray[c].G = this.copiedColorsArray[colorIndex].G
                    this.colorsArray[c].B = this.copiedColorsArray[colorIndex].B
                    colorIndex++
                    c++
                }
            }
            else {
                // let colorIndex = this.copiedColorsArray.length-1
                let colorIndex = 0
                for (let p = 0; p < this.copiedColorsArray.length && c >=0; p++) {
                    console.log('colorIndex: ' + colorIndex)
                    this.colorsArray[c].R = this.copiedColorsArray[colorIndex].R
                    this.colorsArray[c].G = this.copiedColorsArray[colorIndex].G
                    this.colorsArray[c].B = this.copiedColorsArray[colorIndex].B
                    // colorIndex--
                    colorIndex++
                    c--
                }
            }
            this.renderGrid()
            this.$emit('colorDataChanged', { startThreadIndex: startItemIndex, endThreadIndex: c-1 }) // lets the parent know so other draft components like the drawdown can be redrawn
        },

        replaceAllInstancesOfCellColor(itemIndex) {
            let replaceColor = this.colorsArray[itemIndex]
            for (let i=0; i < this.colorsArray.length; i++) {
                if (this.equalRGBColors(this.colorsArray[i], replaceColor)) {
                    let newColor = {}
                    newColor.R = this.currentColor.R
                    newColor.G = this.currentColor.G
                    newColor.B = this.currentColor.B
                    this.colorsArray[i] = newColor
                }
            }
            this.renderGrid()
            this.$emit('colorDataChanged', { startThreadIndex: 0, endThreadIndex: this.colorsArray.length }) // lets the parent know so other draft components like the drawdown can be redrawn
        },

        drawBoundingBox(startItemPos, endItemPos) {
            let startItemIndex = 0
            let endItemIndex = 0

            if (startItemPos <= endItemPos) {
                startItemIndex = startItemPos
                endItemIndex = endItemPos
            }
            else {
                startItemIndex = endItemPos
                endItemIndex = startItemPos
            }

            console.log('startItemIndex: ' + startItemIndex + ', endItemIndex: ' + endItemIndex)

            let startX = 0
            let startY = 0
            let width = 0
            let height = 0

            let colorIndex = this.isVertical ? 0 : this.copiedColorsArray.length-1
            for (let itemNum = startItemIndex; itemNum <= endItemIndex; itemNum++) {
                if (!this.isCopy && !this.isPaste) {
                    this.vueCanvas.fillStyle = 'rgb(' + this.currentColor.R + ',' + this.currentColor.G + ',' + this.currentColor.B + ')'
                }

                console.log('here ' + itemNum)
                if (this.isPaste) {
                    console.log('itemNum: ' + itemNum + ', colorIndexBefore: ' + colorIndex)
                    // let colorIndex = itemNum - startItemIndex
                    let pasteColor = this.copiedColorsArray[colorIndex]
                    this.vueCanvas.fillStyle = 'rgb(' + pasteColor.R + ',' + pasteColor.G + ',' + pasteColor.B + ')'
                    if (this.isVertical) {
                        colorIndex++
                    }
                    else {
                        colorIndex--
                    }
                }

                if (this.isHorizontal) { // must be warp thread colors, horizontal
                    startX = this.pixelXFromColNum(itemNum) + this.borderThicknessPixels
                    startY = this.pixelYFromRowNum(0) + this.borderThicknessPixels
                    height = this.cellPixelSize
                    width = this.cellPixelSize
                }
                else { // must be weft thread colors, vertical
                    startX = this.pixelXFromColNum(0) + this.borderThicknessPixels
                    startY = this.pixelYFromRowNum(itemNum) + this.borderThicknessPixels
                    height = this.cellPixelSize
                    width = this.cellPixelSize
                }
                if (!this.isCopy) {
                    this.vueCanvas.fillRect(startX, startY, width, height)
                }
                // now draw a black box around the cell
                this.vueCanvas.fillStyle = 'rgb(0, 0, 0)'
                this.vueCanvas.fillRect(startX - this.borderThicknessPixels, startY - this.borderThicknessPixels, this.cellPixelSize + (this.borderThicknessPixels * 2), this.borderThicknessPixels)
                this.vueCanvas.fillRect(startX - this.borderThicknessPixels, startY - this.borderThicknessPixels, this.borderThicknessPixels, this.cellPixelSize + (this.borderThicknessPixels * 2))

                this.vueCanvas.fillRect(startX - this.borderThicknessPixels, startY + this.cellPixelSize, this.cellPixelSize + (this.borderThicknessPixels * 2), this.borderThicknessPixels)
                this.vueCanvas.fillRect(startX + this.cellPixelSize, startY - this.borderThicknessPixels, this.borderThicknessPixels, this.cellPixelSize + (this.borderThicknessPixels * 2))
            }
            // console.log('drawBoundingBox startX: ' + startX + ', startY' + startY + ', width: ' + width + ', height: ' + height + ', startItemIndex: ' + startItemIndex + ', endItemIndex: ' + endItemIndex)
        },

        pixelYFromRowNum(rowNum) {
            return rowNum * this.totalCellHeight
        },

        pixelXFromColNum(colNum) {
            return colNum * this.totalCellWidth
        },

        rowNumFromPixelY(y) {
            return Math.trunc(y / this.totalCellHeight)
        },

        colNumFromPixelX(x) {
            return Math.trunc(x / this.totalCellWidth)
        }
    },

    watch: {
    },
    computed: {
        canvasWidth: function() {
            return (this.columnsCount * this.cellPixelSize) + ((this.columnsCount+1) * (this.borderThicknessPixels))
        },

        canvasHeight: function() {
            return (this.rowsCount * this.cellPixelSize) + ((this.rowsCount+1) * (this.borderThicknessPixels))
        },

        totalCellHeight: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        },

        totalCellWidth: function() {
            return this.borderThicknessPixels + this.cellPixelSize
        },

        isHorizontal: function() {
            return (this.rowsCount === 1)
        },

        isVertical: function() {
            return !this.isHorizontal
        }
    },
    created() {
    },
    mounted() {
        let c = this.$refs[this.uniqueCanvasId + this.renderingKey]
        let ctx = c.getContext('2d')
        this.vueCanvas = ctx
        this.renderGrid()
    },
    updated() {
        this.renderGrid()
    }
}
</script>

<style scoped>
  .prevent-scrolling-on-touch {
      touch-action: none
  }
</style>



// WEBPACK FOOTER //
// src/components/ColorsArray.vue